import React, { useState, useEffect } from 'react';
import { FaCheck, FaUsers, FaCalendarAlt, FaVideo, FaEnvelope, FaCloud, FaLock, FaEdit, FaTimes, FaInfoCircle, FaChevronRight, FaStar } from 'react-icons/fa';
import { useAuth } from '../../components/AuthContext';
import { useNavigate } from 'react-router-dom';

const Pricing = () => {
  const { user, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [isAnnual, setIsAnnual] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [exchangeRate, setExchangeRate] = useState(1470); // 기본값 설정

  useEffect(() => {
    const fetchExchangeRate = async () => {
      try {
        const response = await fetch('https://open.er-api.com/v6/latest/USD');
        const data = await response.json();
        if (data && data.rates && data.rates.KRW) {
          setExchangeRate(Math.round(data.rates.KRW));
          console.log("Current USD/KRW Exchange Rate:", data.rates.KRW);
        }
      } catch (error) {
        console.error("Exchange rate fetch error:", error);
      }
    };
    fetchExchangeRate();
  }, []);

  const handlePlanSelection = (plan) => {
    if (plan.isEnterprise) {
      window.location.href = "http://pf.kakao.com/_jyebn/chat";
      return;
    }

    if (!user) {
      navigate("/login", { state: { fromPricing: true, selectedPlan: plan } });
    } else {
      // 로그인된 경우 결제 관리 페이지로 이동하여 카드 등록 유도
      navigate("/payment", { state: { fromPricing: true, selectedPlan: plan } });
    }
  };



  const plans = [
    {
      name: 'Business Starter',
      price: isAnnual ? '6.30' : '7.98',
      originalPrice: isAnnual ? '7.00' : '8.40', // 원래 가격 추가
      features: [
        '맞춤 비즈니스 이메일',
        '화상 회의 (최대 100명)',
        '30GB 클라우드 스토리지',
        '보안 관리 기능',
        '표준 지원',
      ],
      highlight: false,
      color: 'blue',
      badge: '경제적인 시작'
    },
    {
      name: 'Business Standard',
      price: isAnnual ? '12.60' : '15.96',
      originalPrice: isAnnual ? '14.00' : '16.80', // 원래 가격 추가
      features: [
        '맞춤 비즈니스 이메일',
        '화상 회의 (최대 150명)',
        '2TB 클라우드 스토리지',
        '보안 관리 기능',
        '표준 지원',
        '회의 녹화 기능',
      ],
      highlight: true,
      color: 'blue',
      badge: '인기있는 플랜'
    },
    {
      name: 'Business Plus',
      price: isAnnual ? '22.00' : '26.40',
      originalPrice: isAnnual ? '22.00' : '26.40', // 원래 가격 추가
      features: [
        '맞춤 비즈니스 이메일',
        '화상 회의 (최대 250명)',
        '5TB 클라우드 스토리지',
        '고급 보안 기능',
        '강화된 지원',
        'Vault 및 고급 엔드포인트',
      ],
      highlight: false,
      color: 'blue',
      badge: '고급 기능'
    },
    {
      name: 'Enterprise',
      price: '문의',
      originalPrice: '문의', // 원래 가격 추가
      features: [
        '맞춤 비즈니스 이메일',
        '화상 회의 (최대 500명)',
        '무제한 스토리지',
        '최고급 보안 기능',
        '프리미엄 지원',
        'eDiscovery 및 보관',
      ],
      highlight: false,
      color: 'indigo',
      isEnterprise: true,
      badge: '기업 맞춤형'
    }
  ];

  const productivityFeatures = [
    {
      icon: <FaEdit className="w-8 h-8 text-blue-500" />,
      title: '문서 도구',
      description: 'Docs, Sheets, Slides로 실시간 공동 작업',
      features: [
        '실시간 공동 편집',
        '자동 저장 및 버전 관리',
        '오프라인 작업 지원',
        '스마트 작성 도구'
      ]
    },
    {
      icon: <FaVideo className="w-8 h-8 text-blue-500" />,
      title: 'Google Meet',
      description: '안전한 화상 회의 및 음성 통화',
      features: [
        '최대 500명 참석 가능',
        '화면 공유 및 녹화',
        '실시간 자막',
        '노이즈 캔슬링'
      ]
    },
    {
      icon: <FaCalendarAlt className="w-8 h-8 text-blue-500" />,
      title: 'Calendar',
      description: '스마트한 일정 관리 도구',
      features: [
        '일정 자동 조율',
        '회의실 예약',
        '외부 캘린더 통합',
        '스마트 알림'
      ]
    },
    {
      icon: <FaCloud className="w-8 h-8 text-blue-500" />,
      title: 'Drive',
      description: '안전한 클라우드 스토리지',
      features: [
        '파일 실시간 공유',
        '강력한 검색 기능',
        '백업 및 동기화',
        '접근 권한 관리'
      ]
    }
  ];

  const productivityComparison = {
    categories: [
      {
        name: '생산성 도구',
        features: [
          {
            name: '이메일',
            starter: '맞춤 이메일',
            standard: '맞춤 이메일',
            plus: '맞춤 이메일 + eDiscovery',
            enterprise: '맞춤 이메일 + 고급 eDiscovery'
          },
          {
            name: '화상 회의',
            starter: '최대 100명',
            standard: '최대 150명 + 녹화',
            plus: '최대 250명 + 출석 체크',
            enterprise: '최대 500명 + 실시간 번역'
          },
          {
            name: '문서 도구',
            starter: '기본 기능',
            standard: '고급 기능',
            plus: '고급 기능 + 승인 워크플로우',
            enterprise: '맞춤형 워크플로우'
          }
        ]
      },
      {
        name: '협업 도구',
        features: [
          {
            name: 'Google Drive',
            starter: '30GB/사용자',
            standard: '2TB/사용자',
            plus: '5TB/사용자',
            enterprise: '무제한'
          },
          {
            name: 'Calendar',
            starter: '기본 기능',
            standard: '회의실 예약',
            plus: '리소스 분석',
            enterprise: '고급 분석'
          },
          {
            name: 'Chat',
            starter: '기본 채팅',
            standard: '룸 + 파일 공유',
            plus: '고급 검색',
            enterprise: '커스텀 보존 정책'
          }
        ]
      }
    ]
  };

  const tabs = ['모든 플랜 비교', '상세 기능 비교'];

  // 할인율 계산 함수
  const calculateDiscount = (originalPrice, discountedPrice) => {
    if (originalPrice === '문의' || discountedPrice === '문의') return 0;
    return Math.round((1 - parseFloat(discountedPrice) / parseFloat(originalPrice)) * 100);
  };

  // 원화 변환 함수
  const formatKRW = (usdPrice) => {
    if (usdPrice === '문의') return '가격 문의';
    const krw = Math.round(parseFloat(usdPrice) * exchangeRate);
    return new Intl.NumberFormat('ko-KR', { style: 'currency', currency: 'KRW' }).format(krw);
  };


  return (
    <div className="bg-gradient-to-b from-gray-50 to-white py-16 px-4 sm:px-6 lg:px-8">
      {/* 헤더 섹션 */}
      <div className="max-w-7xl mx-auto text-center mb-16">
        <div className="inline-block bg-blue-100 text-blue-700 px-4 py-1 rounded-full font-medium text-sm mb-4">
          Google Workspace
        </div>
        <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
          비즈니스에 맞는 플랜을 선택하세요
        </h1>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-10">
          모든 플랜에는 보안 기능과 협업 도구가 포함되어 있습니다
        </p>

        {/* 결제 주기 토글 - 10명 이상 계약 시 표시 추가 */}
        <div className="inline-flex flex-col items-center">
          <div className="inline-flex items-center justify-center bg-white p-2 rounded-full shadow-md mb-2">
            <button 
              className={`px-6 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                !isAnnual 
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'text-gray-500 hover:text-gray-800'
              }`}
              onClick={() => setIsAnnual(false)}
            >
              약정 없이 결제
            </button>
            <button 
              className={`px-6 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                isAnnual 
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'text-gray-500 hover:text-gray-800'
              }`}
              onClick={() => setIsAnnual(true)}
            >
              <div className="flex items-center">
                1년 약정
                <span className="ml-2 bg-green-100 text-green-700 text-xs px-2 py-0.5 rounded-full">10% 할인</span>
              </div>
            </button>
          </div>
          <p className="text-blue-700 text-sm font-medium bg-blue-50 px-3 py-1 rounded-full">
            1년 약정 및 10명 이상 계약 시 적용되는 가격입니다
          </p>
        </div>
      </div>

      {/* 요금제 그리드 - 디자인 개선 */}
      <div className="max-w-7xl mx-auto mb-24">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {plans.map((plan, index) => (
            <div
              key={index}
              className={`bg-white rounded-2xl overflow-hidden transform transition-all duration-300 hover:shadow-xl ${
                plan.highlight 
                  ? 'shadow-lg ring-2 ring-blue-500 hover:-translate-y-1'
                  : 'shadow-md hover:-translate-y-1 border border-gray-200'
              }`}
            >
              {/* 플랜 헤더 */}
              <div className={`${
                plan.isEnterprise 
                  ? 'bg-gradient-to-r from-indigo-600 to-indigo-800' 
                  : 'bg-gradient-to-r from-blue-500 to-blue-700'
              } px-6 py-4 text-white`}>
                <div className="flex items-center justify-between gap-2">
                  <h3 className="text-lg font-bold whitespace-nowrap">{plan.name}</h3>
                 {plan.badge && (
                  <span className={`
                    ${plan.isEnterprise ? 'bg-indigo-500' : 'bg-blue-400'} 
                    text-white text-[10px] px-2 py-0.5 rounded-full font-medium whitespace-nowrap flex-shrink-0
                  `}>
                    {plan.badge}
                  </span>
                )}
                </div>
              </div>
              
              {/* 가격 정보 - 원래 가격과 할인율 표시 */}
              <div className="px-6 py-5 border-b border-gray-100">
                {plan.price === '문의' ? (
                  <div className="text-2xl font-bold text-gray-800 mb-1">가격 문의</div>
                ) : (
                  <div className="flex flex-col mb-1">
                    <div className="flex items-baseline flex-wrap">
                      <span className="text-2xl font-bold text-gray-900 mr-2">USD {plan.price}</span>
                      <span className="text-blue-600 font-bold ml-auto text-lg">(약 {formatKRW(plan.price)})</span>
                    </div>
                    <span className="text-gray-500 text-xs mt-1">사용자/월</span>
                    <div className="flex items-center mt-2">
                      <span className="text-gray-500 text-xs line-through mr-2">USD {plan.originalPrice}</span>
                      <span className="bg-green-100 text-green-700 text-[10px] px-1.5 py-0.5 rounded-full">
                        {calculateDiscount(plan.originalPrice, plan.price)}% 할인
                      </span>
                    </div>
                  </div>
                )}
                <p className="text-gray-500 text-[10px] mt-2">
                  {plan.isEnterprise ? '대규모 기업 맞춤 가격' : isAnnual ? '1년 약정 시 가격' : '약정 없이 결제 시 가격'}
                </p>
                <p className="text-gray-600 text-sm font-medium mt-1">
                  <span className="text-blue-600">10명 이상 계약 시</span> 적용 가능
                </p>
              </div>
              
              {/* 기능 목록 */}
              <div className="p-6">
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start">
                      <FaCheck className={`w-5 h-5 ${
                        plan.isEnterprise ? 'text-indigo-500' : 'text-blue-500'
                      } mt-0.5 mr-3 flex-shrink-0`} />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
                
                {/* CTA 버튼 */}
                <button
                  onClick={() => handlePlanSelection(plan)}
                  className={`w-full ${
                    plan.isEnterprise
                      ? 'bg-indigo-600 hover:bg-indigo-700'
                      : 'bg-blue-600 hover:bg-blue-700'
                  } text-white py-3 px-6 rounded-lg font-semibold transition-all duration-300 shadow-md hover:shadow-lg flex items-center justify-center`}
                >
                  {plan.isEnterprise ? '영업팀 문의하기' : '지금 구독하기'}
                  <FaChevronRight className="ml-2 text-sm" />
                </button>
              </div>
              
              {/* 강조 플랜 표시 */}
            {plan.highlight && (
              <div className="bg-blue-50 text-blue-700 text-center py-3 border-t border-blue-100">
                <div className="flex items-center justify-center">
                  <FaStar className="text-blue-500 mr-2" />
                  <span className="font-medium">가장 많이 선택하는 플랜</span>
                </div>
              </div>
            )}
            </div>
          ))}
        </div>
      </div>

      {/* 탭 인터페이스 - 비교 탭 추가 */}
      <div className="max-w-7xl mx-auto mb-12">
        <div className="flex justify-center mb-8">
          <div className="inline-flex rounded-lg bg-gray-100 p-1">
            {tabs.map((tab, idx) => (
              <button
                key={idx}
                onClick={() => setActiveTab(idx)}
                className={`px-6 py-2 rounded-lg transition-all duration-200 ${
                  activeTab === idx
                    ? 'bg-white text-blue-700 shadow-sm font-medium'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>

        {/* 탭 콘텐츠 */}
        <div>
          {activeTab === 0 ? (
            <div className="overflow-x-auto bg-white rounded-xl shadow-lg">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="py-4 px-6 text-left text-gray-600">기능</th>
                    <th className="py-4 px-6 text-center text-gray-600">Business Starter</th>
                    <th className="py-4 px-6 text-center text-blue-700 bg-blue-50">
                      <div className="flex flex-col items-center">
                        <span className="inline-block px-2 py-0.5 rounded-full bg-blue-600 text-white text-xs mb-1">인기</span>
                        Business Standard
                      </div>
                    </th>
                    <th className="py-4 px-6 text-center text-gray-600">Business Plus</th>
                    <th className="py-4 px-6 text-center text-indigo-700">Enterprise</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                
                  <tr>
                    <td className="py-4 px-6 font-medium text-gray-700">스토리지</td>
                    <td className="py-4 px-6 text-center">30GB</td>
                    <td className="py-4 px-6 text-center font-medium bg-blue-50">2TB</td>
                    <td className="py-4 px-6 text-center">5TB</td>
                    <td className="py-4 px-6 text-center">무제한</td>
                  </tr>
                  <tr>
                    <td className="py-4 px-6 font-medium text-gray-700">화상 회의</td>
                    <td className="py-4 px-6 text-center">최대 100명</td>
                    <td className="py-4 px-6 text-center font-medium bg-blue-50">최대 150명 + 녹화</td>
                    <td className="py-4 px-6 text-center">최대 250명 + 출석 체크</td>
                    <td className="py-4 px-6 text-center">최대 500명 + 실시간 번역</td>
                  </tr>
                  <tr>
                    <td className="py-4 px-6 font-medium text-gray-700">보안 기능</td>
                    <td className="py-4 px-6 text-center">기본</td>
                    <td className="py-4 px-6 text-center font-medium bg-blue-50">고급</td>
                    <td className="py-4 px-6 text-center">고급 + Vault</td>
                    <td className="py-4 px-6 text-center">최고급 + DLP</td>
                  </tr>
                  <tr>
                    <td className="py-4 px-6 font-medium text-gray-700">사용자 관리</td>
                    <td className="py-4 px-6 text-center">기본</td>
                    <td className="py-4 px-6 text-center font-medium bg-blue-50">고급</td>
                    <td className="py-4 px-6 text-center">세부 관리</td>
                    <td className="py-4 px-6 text-center">최고급 (맞춤형)</td>
                  </tr>
                </tbody>
              </table>
            </div>
          ) : (
            <div>
              {productivityComparison.categories.map((category, categoryIndex) => (
                <div key={categoryIndex} className="mb-12">
                  <h3 className="text-xl font-bold text-gray-800 mb-6 flex items-center">
                    <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center mr-3 text-sm">
                      {categoryIndex + 1}
                    </span>
                    {category.name}
                  </h3>
                  <div className="overflow-x-auto bg-white rounded-xl shadow-lg">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-gray-50">
                          <th className="text-left py-4 px-6 text-gray-600 w-1/5">기능</th>
                          <th className="text-left py-4 px-6 text-gray-600">Starter</th>
                          <th className="text-left py-4 px-6 bg-blue-50 text-blue-700">Standard</th>
                          <th className="text-left py-4 px-6 text-gray-600">Plus</th>
                          <th className="text-left py-4 px-6 text-indigo-700">Enterprise</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {category.features.map((feature, featureIndex) => (
                          <tr key={featureIndex}>
                            <td className="py-4 px-6 font-medium text-gray-700">{feature.name}</td>
                            <td className="py-4 px-6">{feature.starter}</td>
                            <td className="py-4 px-6 bg-blue-50">{feature.standard}</td>
                            <td className="py-4 px-6">{feature.plus}</td>
                            <td className="py-4 px-6">{feature.enterprise}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* 생산성 도구 하이라이트 */}
      <div className="max-w-7xl mx-auto mt-20 mb-20">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-800 mb-4">
            모든 플랜에 포함된 핵심 생산성 도구
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Google Workspace의 강력한 협업 도구로 팀의 생산성을 높이세요
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {productivityFeatures.map((feature, index) => (
            <div 
              key={index} 
              className="bg-white rounded-xl shadow-md p-6 transition-all duration-300 hover:shadow-lg hover:-translate-y-1 border border-gray-200"
            >
              <div className="mb-4 p-3 bg-blue-50 rounded-lg inline-block">
                {feature.icon}
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">{feature.title}</h3>
              <p className="text-gray-600 mb-4">{feature.description}</p>
              <ul className="space-y-2">
                {feature.features.map((item, idx) => (
                  <li key={idx} className="flex items-start text-sm">
                    <FaCheck className="text-blue-500 mt-1 mr-2 flex-shrink-0" />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* FAQ 섹션 추가 */}
      <div className="max-w-4xl mx-auto mt-20 mb-20">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-800 mb-4">
            자주 묻는 질문
          </h2>
        </div>

        <div className="space-y-6">
          {[
            { 
              q: '구독은 언제든지 취소할 수 있나요?', 
              a: '네, 언제든지 구독을 취소할 수 있습니다. 월간 구독의 경우 다음 결제일 전에, 연간 구독의 경우 계약 기간 종료 시 자동 갱신을 중지할 수 있습니다.'
            },
            { 
              q: '무료 체험 기간은 얼마나 되나요?', 
              a: '모든 플랜에 대해 14일 무료 체험 기간을 제공합니다. 체험 기간 동안 신용카드 정보는 필요하지 않습니다.'
            },
            { 
              q: '사용자 수는 나중에 변경할 수 있나요?', 
              a: '네, 언제든지 사용자를 추가하거나 제거할 수 있습니다. 사용자 추가 시 비례 배분된 요금이 청구됩니다.'
            },
            { 
              q: '기존 이메일을 Google Workspace로 마이그레이션할 수 있나요?', 
              a: '네, Google Workspace는 기존 이메일, 연락처, 캘린더 데이터를 마이그레이션하기 위한 도구를 제공합니다.'
            },
            { 
              q: '10명 미만으로 계약할 수 있나요?',
              a: '네, 10명 미만으로도 계약이 가능합니다만, 표시된 할인 가격은 10명 이상 계약 시에만 적용됩니다. 자세한 내용은 영업팀에 문의해 주세요.'
            }
          ].map((faq, index) => (
            <div key={index} className="bg-white rounded-xl shadow-md overflow-hidden">
              <div className="px-6 py-4 border-l-4 border-blue-500">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-semibold text-gray-800">{faq.q}</h3>
                  <FaInfoCircle className="text-blue-500" />
                </div>
                <div className="mt-2 text-gray-600">
                  {faq.a}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* CTA 섹션 개선 */}
      <div className="max-w-5xl mx-auto mt-24 text-center">
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl p-12 shadow-xl text-white relative overflow-hidden">
          {/* 배경 요소 */}
          <div className="absolute top-0 left-0 w-full h-full opacity-10">
            <div className="absolute top-10 left-10 w-32 h-32 rounded-full bg-white"></div>
            <div className="absolute bottom-10 right-10 w-40 h-40 rounded-full bg-blue-300"></div>
          </div>
          
          <div className="relative z-10">
            <h2 className="text-3xl font-bold mb-6 text-white">
              지금 바로 비즈니스를 혁신하세요
            </h2>
            <p className="text-xl opacity-90 mb-4 w-full mx-auto text-white">
              파트너사를 통해 <span className="bg-blue-500 bg-opacity-30 px-2 py-1 rounded font-bold">30일 무료 체험</span>으로 Google Workspace의 모든 기능을 경험해보세요
            </p>
            <p className="text-sm opacity-80 mb-10 max-w-2xl mx-auto">
              일반 신청 시 14일, 파트너사 통해 신청 시 30일 무료 체험 제공
            </p>
            <div className="flex justify-center">
              <a 
                href="http://pf.kakao.com/_jyebn/chat" 
                target="_blank" 
                rel="noopener noreferrer"
                className="block"
              >
                <button className="bg-white text-blue-600 px-10 py-4 rounded-lg font-bold hover:bg-blue-50 transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:-translate-y-1 flex items-center">
                  <span>30일 무료 체험 및 전문가 상담</span>
                  <FaChevronRight className="ml-2" />
                </button>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Pricing;